import * as React from 'react'
import { cn } from '@/lib/utils'
import { Icon, type IconProps, type IconSize } from './Icon'
import { iconMap, type IconName } from './iconMap'

export interface DynamicIconProps extends Omit<IconProps, 'name'> {
  /**
   * Preferred source. If it matches an entry in `iconMap`, renders as a Lucide `<Icon>`.
   * Otherwise falls back to `fallbackValue` or renders nothing.
   */
  name?: string | null
  /**
   * Fallback value (usually a legacy emoji string from the backend).
   * Rendered inline as a text node with matching size when `name` is not a known icon.
   */
  fallbackValue?: string | null
  /** Tailwind class applied on the fallback span. */
  fallbackClassName?: string
}

function isIconName(n: string): n is IconName {
  return Object.prototype.hasOwnProperty.call(iconMap, n)
}

const fallbackSizeClass: Record<IconSize, string> = {
  12: 'text-xs',
  14: 'text-sm',
  16: 'text-base',
  20: 'text-xl',
  24: 'text-2xl',
  32: 'text-3xl',
  40: 'text-4xl',
  48: 'text-5xl',
  64: 'text-6xl',
}

/**
 * Renderiza um ícone a partir de um valor dinâmico (vindo de backend/user).
 * Se `name` é um Lucide icon conhecido → renderiza `<Icon>`.
 * Senão, renderiza `fallbackValue` (tipicamente emoji) inline.
 *
 * Facilita migração gradual de `snippet.icon = '🎫'` para `snippet.iconName = 'ticket'`
 * sem quebrar dados já persistidos.
 */
export const DynamicIcon: React.FC<DynamicIconProps> = ({
  name,
  fallbackValue,
  fallbackClassName,
  size = 20,
  className,
  ...rest
}) => {
  if (name && isIconName(name)) {
    return <Icon name={name} size={size} className={className} {...rest} />
  }
  if (fallbackValue) {
    return (
      <span
        aria-hidden="true"
        className={cn(
          'inline-flex items-center justify-center',
          fallbackSizeClass[size],
          fallbackClassName,
          className
        )}
      >
        {fallbackValue}
      </span>
    )
  }
  return null
}

DynamicIcon.displayName = 'DynamicIcon'
